import {
  css,
  type DefaultTheme,
  type FlattenInterpolation,
  type Interpolation,
  type ThemedStyledProps,
} from 'styled-components'

import { breakpoints } from './breakpoints'

/** Tagged-template function whose interpolations know component props <P>. */
type MediaQuery = <P extends object>(
  first: TemplateStringsArray,
  ...interpolations: Interpolation<ThemedStyledProps<P, DefaultTheme>>[]
) => FlattenInterpolation<ThemedStyledProps<P, DefaultTheme>>

const breakpointKeys = Object.keys(breakpoints) as Array<
  keyof typeof breakpoints
>

/** Simple map of breakpoint > raw `@media` string. */
type MediaQueryStrings = Record<keyof typeof breakpoints, string>

/** Mutable map of breakpoint > fully-typed `MediaQuery` function. */
type Media = {
  -readonly [K in keyof typeof breakpoints]: MediaQuery
}

/** `${media.lg``}` – adds `@media (min-width)` around the block. */
export const media = breakpointKeys.reduce<Partial<Media>>((acc, label) => {
  acc[label] = <P extends object>(
    first: TemplateStringsArray,
    ...interps: Interpolation<ThemedStyledProps<P, DefaultTheme>>[]
  ) => css<P>`
    @media (min-width: ${breakpoints[label]}px) {
      ${css<P>(first, ...interps)};
    }
  `
  return acc
}, {}) as Media

/** `${mediaMax.lg``}` – adds `@media (max-width)` around the block. */
export const mediaMax = breakpointKeys.reduce<Partial<Media>>((acc, label) => {
  acc[label] = <P extends object>(
    first: TemplateStringsArray,
    ...interps: Interpolation<ThemedStyledProps<P, DefaultTheme>>[]
  ) => css<P>`
    @media (max-width: ${breakpoints[label] - 1}px) {
      ${css<P>(first, ...interps)};
    }
  `
  return acc
}, {}) as Media

/** `mediaObj.lg` > `"@media (min-width: 992px)"` */
export const mediaObj = breakpointKeys.reduce<MediaQueryStrings>(
  (acc, label) => {
    acc[label] = `@media (min-width: ${breakpoints[label]}px)`
    return acc
  },
  {} as MediaQueryStrings
)

/** `mediaMaxObj.lg` > `"@media (max-width: 991px)"` */
export const mediaMaxObj = breakpointKeys.reduce<MediaQueryStrings>(
  (acc, label) => {
    acc[label] = `@media (max-width: ${breakpoints[label] - 1}px)`
    return acc
  },
  {} as MediaQueryStrings
)
